import React, { useState, useEffect } from 'react';
import { Card, Table, Rate, Tag, Statistic, Row, Col, Typography, Space, Button, Modal, message } from 'antd';
import { StarOutlined, TrophyOutlined, RiseOutlined, AlertOutlined } from '@ant-design/icons';
import { dishRatingsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { categoryLabel } from '../utils/labels';

const { Title, Text } = Typography;

interface DishAnalytics {
  menuItem: {
    id: number;
    name: string;
    category: string;
  };
  avgRating: number;
  totalRatings: number;
  distribution: {
    fiveStar: number;
    fourStar: number;
    threeStar: number;
    twoStar: number;
    oneStar: number;
  };
  lastUpdated: string;
}

interface Escalation {
  id: number;
  rating: number;
  review: string;
  escalationLevel: string;
  companyName?: string;
  createdAt: string;
  menuItem: {
    id: number;
    name: string;
    supplier: {
      companyName: string;
    };
  };
  user?: {
    username: string;
    customer: {
      companyName: string;
    };
  };
}

const DishRatingsAnalytics: React.FC = () => {
  const { user } = useAuthStore();
  const isMobile = useIsMobile();
  const [analytics, setAnalytics] = useState<DishAnalytics[]>([]);
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [escalationModalVisible, setEscalationModalVisible] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState<Escalation | null>(null);

  useEffect(() => {
    loadAnalytics();
    loadEscalations();
  }, [user?.role, user?.supplierId]);

  const loadAnalytics = async () => {
    try {
      const supplierId = user?.supplierId;
      if (user?.role !== 'supplier' || supplierId == null) {
        setAnalytics([]);
        return;
      }
      const response = await dishRatingsAPI.getSupplierAnalytics(supplierId);
      setAnalytics(response.analytics || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadEscalations = async () => {
    try {
      const response = await dishRatingsAPI.getEscalations();
      setEscalations(response.escalations || []);
    } catch (error) {
      console.error('Error loading escalations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEscalationUpdate = async (ratingId: number, newLevel: string) => {
    try {
      await dishRatingsAPI.updateEscalation(ratingId, newLevel);
      message.success('Уровень эскалации обновлен');
      loadEscalations();
      setEscalationModalVisible(false);
    } catch (error) {
      message.error('Ошибка при обновлении эскалации');
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return 'green';
    if (rating >= 3.5) return 'blue';
    if (rating >= 2.5) return 'orange';
    return 'red';
  };

  const getEscalationColor = (level: string) => {
    switch (level) {
      case 'none': return 'default';
      case 'company': return 'orange';
      case 'customer': return 'red';
      case 'supplier': return 'purple';
      default: return 'default';
    }
  };

  const getEscalationText = (level: string) => {
    switch (level) {
      case 'none': return 'Нет эскалации';
      case 'company': return 'Уведомление компании';
      case 'customer': return 'Эскалация к заказчику';
      case 'supplier': return 'Прямое обращение';
      default: return level;
    }
  };

  const analyticsColumns = [
    {
      title: 'Блюдо',
      dataIndex: 'menuItem',
      key: 'menuItem',
      render: (menuItem: any) => (
        <div>
          <Text strong>{menuItem.name}</Text>
          <br />
          <Tag color="blue">{categoryLabel(menuItem.category)}</Tag>
        </div>
      ),
    },
    {
      title: 'Средний рейтинг',
      dataIndex: 'avgRating',
      key: 'avgRating',
      render: (rating: number) => (
        <Space>
          <Rate disabled value={rating} style={{ fontSize: '16px' }} />
          <Text strong>{rating.toFixed(1)}</Text>
        </Space>
      ),
      sorter: (a: any, b: any) => a.avgRating - b.avgRating,
    },
    {
      title: 'Количество оценок',
      dataIndex: 'totalRatings',
      key: 'totalRatings',
      render: (count: number) => <Text>{count}</Text>,
      sorter: (a: any, b: any) => a.totalRatings - b.totalRatings,
    },
    {
      title: 'Распределение',
      key: 'distribution',
      render: (record: DishAnalytics) => (
        <div style={{ fontSize: '12px' }}>
          <div>⭐ {record.distribution.fiveStar}</div>
          <div>⭐ {record.distribution.fourStar}</div>
          <div>⭐ {record.distribution.threeStar}</div>
          <div>⭐ {record.distribution.twoStar}</div>
          <div>⭐ {record.distribution.oneStar}</div>
        </div>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      render: (record: DishAnalytics) => (
        <Tag color={getRatingColor(record.avgRating)}>
          {record.avgRating >= 4.5 ? 'Отлично' : 
           record.avgRating >= 3.5 ? 'Хорошо' : 
           record.avgRating >= 2.5 ? 'Удовлетворительно' : 'Требует внимания'}
        </Tag>
      ),
    },
  ];

  const escalationColumns = [
    {
      title: 'Блюдо',
      dataIndex: 'menuItem',
      key: 'menuItem',
      render: (menuItem: any) => <Text strong>{menuItem.name}</Text>,
    },
    {
      title: 'Оценка',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number) => (
        <Rate disabled value={rating} style={{ fontSize: '16px' }} />
      ),
    },
    {
      title: 'Отзыв',
      dataIndex: 'review',
      key: 'review',
      render: (review: string) => (
        <Text ellipsis={{ tooltip: review }} style={{ maxWidth: 200 }}>
          {review}
        </Text>
      ),
    },
    {
      title: 'Компания',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (companyName: string, record: Escalation) => (
        <Text>{companyName || record.user?.customer?.companyName || 'Анонимно'}</Text>
      ),
    },
    {
      title: 'Эскалация',
      dataIndex: 'escalationLevel',
      key: 'escalationLevel',
      render: (level: string) => (
        <Tag color={getEscalationColor(level)}>
          {getEscalationText(level)}
        </Tag>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (record: Escalation) => (
        <Button 
          size="small" 
          onClick={() => {
            setSelectedEscalation(record);
            setEscalationModalVisible(true);
          }}
        >
          Управление
        </Button>
      ),
    },
  ];

  const totalStats = analytics.reduce((acc, item) => ({
    totalRatings: acc.totalRatings + item.totalRatings,
    avgRating: acc.avgRating + item.avgRating
  }), { totalRatings: 0, avgRating: 0 });

  const overallAvgRating = analytics.length > 0 ? totalStats.avgRating / analytics.length : 0;

  return (
    <div style={{ padding: isMobile ? 0 : '24px' }}>
      <Title level={isMobile ? 4 : 2}>
        <StarOutlined style={{ marginRight: '8px' }} />
        Аналитика оценок блюд
      </Title>

      {/* Общая статистика */}
      <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Общий рейтинг"
              value={overallAvgRating}
              precision={1}
              suffix="/ 5"
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Всего оценок"
              value={totalStats.totalRatings}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Блюд в меню"
              value={analytics.length}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Требуют внимания"
              value={escalations.length}
              prefix={<AlertOutlined />}
              valueStyle={{ color: escalations.length > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Аналитика по блюдам */}
      <Card title="Рейтинг блюд" style={{ marginBottom: '24px' }}>
        <Table
          columns={analyticsColumns}
          dataSource={analytics}
          rowKey="menuItem.id"
          pagination={{ pageSize: 10 }}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 'max-content' : undefined }}
        />
      </Card>

      {/* Эскалации */}
      <Card title="Требуют внимания" extra={
        <Button 
          type="primary" 
          icon={<AlertOutlined />}
          onClick={loadEscalations}
        >
          Обновить
        </Button>
      }>
        <Table
          columns={escalationColumns}
          dataSource={escalations}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 'max-content' : undefined }}
        />
      </Card>

      {/* Модальное окно управления эскалацией */}
      <Modal
        title="Управление эскалацией"
        open={escalationModalVisible}
        onCancel={() => setEscalationModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 520}
        centered={!isMobile}
      >
        {selectedEscalation && (
          <div>
            <p><strong>Блюдо:</strong> {selectedEscalation.menuItem.name}</p>
            <p><strong>Оценка:</strong> <Rate disabled value={selectedEscalation.rating} /></p>
            <p><strong>Отзыв:</strong> {selectedEscalation.review}</p>
            <p><strong>Компания:</strong> {selectedEscalation.companyName || 'Анонимно'}</p>
            
            <div style={{ marginTop: '16px' }}>
              <Text strong>Изменить уровень эскалации:</Text>
              <div style={{ marginTop: '8px' }}>
                <Button 
                  onClick={() => handleEscalationUpdate(selectedEscalation.id, 'none')}
                  style={{ marginRight: '8px' }}
                >
                  Снять эскалацию
                </Button>
                <Button 
                  onClick={() => handleEscalationUpdate(selectedEscalation.id, 'customer')}
                  style={{ marginRight: '8px' }}
                >
                  К заказчику
                </Button>
                <Button 
                  onClick={() => handleEscalationUpdate(selectedEscalation.id, 'supplier')}
                >
                  Прямое обращение
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DishRatingsAnalytics;
